import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule, NonNullableFormBuilder, AbstractControl, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, filter, of, delay, catchError, finalize, tap, lastValueFrom } from 'rxjs';
import { OrderStatusResponse, OrderResponse, ChangeOrderStatusRequest } from '../../../models/orders/order.model';
import { PlaceResponse } from '../../../models/orders/place.model';
import { FilterStatusesPipe } from '../../../pipes/filter-statuses.pipe';
import { OrderService } from '../../../services/order.service';
import { PlaceService } from '../../../services/place.service';
import { DisplayPlaceFieldComponent } from '../../places/display-place-field/display-place-field.component';
import { ScanPlaceCodeComponent } from '../../places/scan-place-code/scan-place-code.component';
import { SearchPlaceDialogComponent } from '../../places/search-place-dialog/search-place-dialog.component';
import { QrScannerComponent } from '../../scanner-dialog/qr-scanner/qr-scanner.component';
import { SearchSingleOrderComponent } from '../search-single-order/search-single-order.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { SelectStatusDialogComponent } from '../order-statuses/select-status-dialog/select-status-dialog.component';

// Custom Interfaces
interface OrderTransferFormRow {
  isPlaceholder: FormControl<boolean>;
  orderId: FormControl<string>; // Actual Order ID (GUID) - Populated on identification
  orderRef: FormControl<number | null>;
  animalName: FormControl<string>;
  currentOrderStatusDisplay: FormControl<string | null>; // For displaying the current status name
  placeId: FormControl<string | null>; // Target Place ID for the transfer
  targetOrderStatusId: FormControl<string | null>; // Target OrderStatus ID for the transfer
  validStatusTransitions: FormControl<string[]>; // Valid NEXT OrderStatus IDs for this order
  isProcessing: FormControl<boolean>; // Covers identification and submission processing
  isSuccess: FormControl<boolean>; // For submission success
  errorMessage: FormControl<string | null>;
}
type OrderTransferFormGroup = FormGroup<OrderTransferFormRow>;


@Component({
  selector: 'app-transfer-form', // Selector for the component
  standalone: true,
  imports: [
    // Import necessary modules for template usage
    CommonModule, 
    ReactiveFormsModule, 
    MatTableModule, 
    MatFormFieldModule, 
    MatSelectModule, 
    MatInputModule,
    MatButtonModule, 
    MatIconModule, 
    MatProgressSpinnerModule, 
    MatTooltipModule, 
    MatDialogModule,
    DisplayPlaceFieldComponent, 
    FilterStatusesPipe, 
    MatCheckboxModule,

  ],
  templateUrl: './transfer-form.component.html',
  styleUrl: './transfer-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransferFormComponent implements OnInit {
  // --- Injected Services ---
  private fb = inject(NonNullableFormBuilder);
  private cd = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private placeService = inject(PlaceService);
  private orderService = inject(OrderService);
  private toastr = inject(ToastrService);
  // No ScheduleService needed here

  // --- Component Inputs ---
  @Input() initialOrders: OrderResponse[] = [];

  // --- Component State ---
  public dataSource = new MatTableDataSource<AbstractControl>([]);
  public transferForm: FormGroup<{ orders: FormArray<OrderTransferFormGroup> }>;
  public selection = new SelectionModel<OrderTransferFormGroup>(true, []);
  public isLoading = false; 
  public isSubmitting = false; 
  public allOrderStatuses: OrderStatusResponse[] = []; 

  // --- Configuration ---
  public readonly displayedColumns: string[] = [
    'select',
    'rowStatus',                  // Processing/Success/Error indicator
    'orderRef',                   // Order Ref# or Identify Buttons
    'animalName',                 // Animal Name
    'currentOrderStatusDisplay',  // Display Current Order Status Name
    'processingPlace',            // Select/Scan/Display Place
    'targetOrderStatus',          // Select Target Order Status
    'actions'                     // Remove row button
  ];
  // No validSchedulingStatuses needed here

  // --- Lifecycle Hooks ---
  constructor() {
    // Initialize the main form structure
    this.transferForm = this.fb.group({
      orders: this.fb.array<OrderTransferFormGroup>([]), // Initialize empty FormArray
    });
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this.cd.markForCheck();
    try {
      // Fetch all possible order statuses needed for dropdowns etc.
      const statusesLoaded = await this.fetchAllOrderStatuses();
      if (!statusesLoaded) {
        throw new Error("Initialization failed: Could not load necessary order statuses.");
      }
      this.initializeForm(this.initialOrders);

    } catch (error) {
      console.error("Error during component initialization:", error);
      this.toastr.error("Failed to initialize component.", "Initialization Error");
    } finally {
      this.isLoading = false;
      this.cd.markForCheck();
    }
  }

  // --- Form Array Accessor ---
  public get ordersFormArray(): FormArray<OrderTransferFormGroup> {
    return this.transferForm.get('orders') as FormArray<OrderTransferFormGroup>;
  }

  // --- Data Loading & Setup ---

  /** Fetches all possible order statuses once, assigns the result, and returns a promise indicating success. */
  private async fetchAllOrderStatuses(): Promise<boolean> {
    let success = false;
    try {
      const statusPagedResult = await firstValueFrom(this.orderService.getOrderStatuses());
      this.allOrderStatuses = statusPagedResult?.items || [];
      success = true;
    } catch (err) {
      console.error("Error fetching all order statuses from service:", err);
      this.toastr.error("Could not load the list of possible order statuses.", "Error");
      this.allOrderStatuses = [];
      success = false;
    }
    return success;
  }

  /** Initializes the form array with given orders (or starts empty) */
  private initializeForm(orders: OrderResponse[]): void {
    const formGroups = orders.map(order => this.createOrderTransferFormGroup(order));
    const formArray = this.fb.array(formGroups);
    this.transferForm.setControl('orders', formArray);
    this.updateDataSource();
    // TODO: Fetch initial valid transitions if initializing with orders
    // await this.fetchInitialValidTransitions();
  }

  /** Creates a FormGroup for a single row in the table */
  private createOrderTransferFormGroup(order: OrderResponse | null): OrderTransferFormGroup {
    const isPlaceholder = !order;
    // For identified rows, get current status details
    const currentOrderStatusId = order?.status?.id || '';
    const currentOrderStatusName = order?.status?.name || null;
    const currentPlaceId = order?.currentPlace?.id || null;
    const validTransitions = order?.status?.validTransitions || [];

    return this.fb.group<OrderTransferFormRow>({
      isPlaceholder: this.fb.control<boolean>(isPlaceholder),
      orderId: this.fb.control<string>(order?.id || ''), // Order GUID
      orderRef: this.fb.control<number | null>(order?.referenceNumber || null),
      animalName: this.fb.control<string>(order?.animalName || ''),
      currentOrderStatusDisplay: this.fb.control<string | null>(currentOrderStatusName), // Display only
      placeId: this.fb.control<string | null>(currentPlaceId, Validators.required), // Target Place ID
      targetOrderStatusId: this.fb.control<string | null>(validTransitions[0] ?? null, Validators.required), // Target Order Status ID (start empty)
      validStatusTransitions: this.fb.control<string[]>(validTransitions), // Store valid transitions if order provided
      isProcessing: this.fb.control<boolean>(false),
      isSuccess: this.fb.control<boolean>(false),
      errorMessage: this.fb.control<string | null>(null)
    });
  }

  // --- Add / Remove Rows ---
  public addOrderRow(): void {
    // Add a new placeholder row
    const newRowFormGroup = this.createOrderTransferFormGroup(null);
    this.ordersFormArray.push(newRowFormGroup);
    this.updateDataSource();
    this.cd.markForCheck();
  }

  public removeOrderRow(index: number): void {
    if (index >= 0 && index < this.ordersFormArray.length) {
      this.ordersFormArray.removeAt(index);
      this.updateDataSource();
      this.cd.markForCheck();
    }
  }

  // --- Update Table Data Source ---
  private updateDataSource(): void {
    this.dataSource.data = this.ordersFormArray.controls;
  }

  // --- === Order Identification Logic (Stubs) === ---

  /** Opens the Order Search Dialog for a specific placeholder row */
  public openOrderSearchDialog(index: number): void {
    const rowGroup = this.ordersFormArray.at(index);
    if (!rowGroup || !rowGroup.value.isPlaceholder || rowGroup.value.isProcessing || this.isSubmitting) return;

    const dialogRef = this.dialog.open(SearchSingleOrderComponent, { width: '400px', disableClose: true, autoFocus: true });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== null && result !== undefined && result !== '') {
        const orderNumber = Number(result);
        if (!isNaN(orderNumber)) {
          this.identifyOrder(orderNumber, index);
        } else {
          this.toastr.error(`Invalid order number format: '${result}'.`, "Invalid Input");
        }
      }
    });
  }

  /** Opens the QR Scanner Dialog for identifying an order for a specific placeholder row */
  public openOrderScanDialog(index: number): void {
    const rowGroup = this.ordersFormArray.at(index);
    if (!rowGroup || !rowGroup.value.isPlaceholder || rowGroup.value.isProcessing || this.isSubmitting) return;

    const dialogRef = this.dialog.open(QrScannerComponent, { width: '500px', disableClose: true });
    dialogRef.afterClosed().subscribe(result => {
      if (result && typeof result === 'string') {
        this.identifyOrder(result, index);
      } else if (result) {
        this.toastr.warning("Invalid data received from scanner.", "Scan Warning");
      }
    });
  }

  /** Fetches Order details via OrderService and populates the row */
  private async identifyOrder(identifier: string | number, index: number): Promise<void> {
    const rowGroup = this.ordersFormArray.at(index);
    if (!rowGroup) return;

    rowGroup.controls.isProcessing.setValue(true); rowGroup.controls.errorMessage.setValue(null); this.cd.markForCheck();

    try {
      let orderIdToFetch: string | null = null;
      if (typeof identifier === 'string') {
          orderIdToFetch = identifier; // Assume scan gives Order ID directly? Needs confirmation.
      } else if (typeof identifier === 'number') {
        orderIdToFetch = identifier.toString(); // Ensure it's a string for the service call
      }
      
      if (!orderIdToFetch) {
          throw new Error(`Could not determine Order ID from identifier: ${identifier}`);
      }

      // Check for duplicates among already identified orders
      const existingIdentifiedIndex = this.ordersFormArray.controls.findIndex((ctrl, idx) =>
          idx !== index && !ctrl.value.isPlaceholder && ctrl.value.orderId === orderIdToFetch
      );
      if (existingIdentifiedIndex !== -1) {
          throw new Error(`Order with ID ${orderIdToFetch} (Ref#: ${identifier}) is already identified.`);
      }

      // Fetch full Order details
      const orderResult = await firstValueFrom(this.orderService.getOrderByReference(orderIdToFetch));
      if (!orderResult) { throw new Error(`Could not retrieve details for Order ID: ${orderIdToFetch}.`); }

      // Patch the row with fetched data
      rowGroup.patchValue({
        isPlaceholder: false,
        orderId: orderResult.id,
        orderRef: orderResult.referenceNumber,
        animalName: orderResult.animalName,
        currentOrderStatusDisplay: orderResult.status?.name || null, // Display current status name
        placeId: orderResult.currentPlace?.id || null, // Use current place from OrderResponse
        targetOrderStatusId: orderResult.status?.validTransitions[0] || null, // Reset target status upon identification
        validStatusTransitions: orderResult.status?.validTransitions || [], // Store valid transitions
        isProcessing: false, isSuccess: false, errorMessage: null
      });
      this.toastr.success(`Order #${orderResult.referenceNumber} identified.`);

    } catch (err: any) {
      this.handleIdentificationError(rowGroup, err?.message || `Failed to identify order.`);
    } finally {
      if (rowGroup.value.isProcessing) { rowGroup.controls.isProcessing.setValue(false); }
      this.cd.markForCheck();
    }
  }

  /** Helper to handle errors during order identification */
  private handleIdentificationError(rowGroup: OrderTransferFormGroup, errorMessage: string): void {
    rowGroup.controls.isProcessing.setValue(false);
    rowGroup.controls.errorMessage.setValue(errorMessage);
    this.toastr.error(errorMessage, 'Order Identification Failed');
    // Caller handles markForCheck
  }
  // --- === End: Order Identification Logic === ---

  // --- Selection Helpers ---

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  public toggleAllRows(): void {
    this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach(row => this.selection.select(row as OrderTransferFormGroup));
  }

  // -- Bulk Actions --

  public openBulkPlaceDialog(): void {
    if (!this.selection.hasValue()) {
      this.toastr.info("No rows selected for bulk action.");
      return;
    }

    // Use the same search dialog as the single row action
    const dialogRef = this.dialog.open(SearchPlaceDialogComponent, {
      width: '600px',
      disableClose: true,
      autoFocus: false // Prevent focus trap issues sometimes
    });

    dialogRef.afterClosed()
      .pipe(filter((result): result is PlaceResponse => !!result)) // Ensure we have a PlaceResponse object
      .subscribe(selectedPlace => {
        let updatedCount = 0;
        this.selection.selected.forEach((rowGroup) => {
          // Apply only to rows that are identified and not already processed/processing
          if (!rowGroup.value.isPlaceholder && !rowGroup.value.isProcessing && !rowGroup.value.isSuccess) {
            rowGroup.controls.placeId.setValue(selectedPlace.id);
            rowGroup.controls.placeId.markAsTouched(); // Mark as touched for validation if needed
            updatedCount++;
          }
        });

        if (updatedCount > 0) {
          this.toastr.success(`Set place to "${selectedPlace.name}" for ${updatedCount} selected row(s).`);
          this.cd.markForCheck(); // Trigger change detection to update the view
        } else {
            this.toastr.info("No applicable rows found to update the place.", "Bulk Action");
        }
      });
  }

  public openBulkStatusDialog(): void {
    if (!this.selection.hasValue()) {
      this.toastr.info("No rows selected for bulk action.");
      return;
    }

    const validBulkStatuses = this.getValidBulkTransitionStatuses();

    if (validBulkStatuses.length === 0) {
      this.toastr.warning("No common target status available for all selected rows.", "Bulk Status Unavailable");
      return;
    }

    const dialogRef = this.dialog.open(SelectStatusDialogComponent, {
      width: '400px',
      data: { statuses: validBulkStatuses },
      disableClose: true
    });

    dialogRef.afterClosed()
      .pipe(filter((result): result is OrderStatusResponse => !!result)) // Ensure we have an OrderStatusResponse
      .subscribe(selectedStatus => {
        let updatedCount = 0;
        this.selection.selected.forEach((rowGroup) => {
          // Apply only to rows that are identified and not already processed/processing
          if (!rowGroup.value.isPlaceholder && !rowGroup.value.isProcessing && !rowGroup.value.isSuccess) {
            // Double-check if this status is indeed valid for this specific row (should be, due to intersection logic)
            if ((rowGroup.value.validStatusTransitions || []).includes(selectedStatus.id)) {
                 rowGroup.controls.targetOrderStatusId.setValue(selectedStatus.id);
                 rowGroup.controls.targetOrderStatusId.markAsTouched();
                 updatedCount++;
             } else {
                 // This case should ideally not happen if getValidBulkTransitionStatuses logic is correct
                 console.warn(`Row ${rowGroup.value.orderRef} was selected but cannot transition to bulk status ${selectedStatus.name}. Skipping.`);
             }
          }
        });

        if (updatedCount > 0) {
          this.toastr.success(`Set target status to "${selectedStatus.name}" for ${updatedCount} selected row(s).`);
          this.cd.markForCheck(); // Trigger change detection
        } else {
             this.toastr.info("No applicable rows found to update the status.", "Bulk Action");
        }
      });
  }


  // -- Bulk Actions Helpers --

  private getValidBulkTransitionStatuses(): OrderStatusResponse[] {
    if (!this.selection.hasValue()) {
      return [];
    }

    const applicableRows = this.selection.selected.filter(row =>
      !row.value.isPlaceholder && !row.value.isProcessing && !row.value.isSuccess
    );

    if (applicableRows.length === 0) {
      return []; // No valid rows selected to determine common statuses
    }

    // Start with the valid transitions of the first applicable row
    let commonTransitionIds = new Set<string>(applicableRows[0].value.validStatusTransitions || []);

    // Find intersection with subsequent rows
    for (let i = 1; i < applicableRows.length; i++) {
      const currentRowTransitions = new Set<string>(applicableRows[i].value.validStatusTransitions || []);
      commonTransitionIds.forEach(id => {
        if (!currentRowTransitions.has(id)) {
          commonTransitionIds.delete(id); // Remove if not present in the current row's transitions
        }
      });
      // Optimization: if intersection becomes empty, no need to check further
      if (commonTransitionIds.size === 0) break;
    }

    // Convert the final set of IDs back to OrderStatusResponse objects
    return this.allOrderStatuses.filter(status => commonTransitionIds.has(status.id));
  }


  // --- Place Handling Logic (Stubs - Adapt as needed) ---
  public openPlaceSearchDialog(index: number): void {
     const rowGroup = this.ordersFormArray.at(index);
     if (!rowGroup || rowGroup.value.isPlaceholder || this.isSubmitting || rowGroup.value.isProcessing) return;
     const dialogRef = this.dialog.open(SearchPlaceDialogComponent, { width: '600px', disableClose: true, autoFocus: false });
     dialogRef.afterClosed().pipe(filter((r): r is PlaceResponse => !!r)).subscribe(p => {
         rowGroup.controls.placeId.setValue(p.id); rowGroup.controls.placeId.markAsTouched(); this.cd.markForCheck();
     });
  }

  public openPlaceScanDialog(index: number): void {
     const rowGroup = this.ordersFormArray.at(index);
     if (!rowGroup || rowGroup.value.isPlaceholder || this.isSubmitting || rowGroup.value.isProcessing) return;
     const dialogRef = this.dialog.open(ScanPlaceCodeComponent, { width: '500px' }); // Assuming ScanPlaceCodeComponent returns PlaceResponse
     dialogRef.afterClosed().pipe(filter((r): r is PlaceResponse => !!r && typeof r === 'object' && 'id' in r))
     .subscribe({
         next: (p) => { rowGroup.controls.placeId.setValue(p.id); rowGroup.controls.placeId.markAsTouched(); this.cd.markForCheck(); },
         error: (err) => { console.error("Error after place scan dialog:", err); this.toastr.error("Unexpected error after scanning.", "Error");}
     });
  }

  public clearPlace(index: number): void {
     const rowGroup = this.ordersFormArray.at(index);
     if (!rowGroup || rowGroup.value.isPlaceholder || this.isSubmitting || rowGroup.value.isProcessing) return;
     rowGroup.controls.placeId.setValue(null);
     rowGroup.controls.placeId.markAsUntouched();
     this.cd.markForCheck();
  }
  // --- End Place Handling ---


  // --- Form Submission ---
  public async onSubmit(): Promise<void> {
    if (!this.transferForm.valid || this.isSubmitting) {
      if (this.transferForm.invalid) { this.toastr.error('Please correct invalid fields.', 'Invalid Form'); this.transferForm.markAllAsTouched();}
      return;
    }
    const controlsToProcessData = this.transferForm.getRawValue().orders.filter(row => !row.isPlaceholder);
    const controlsInFormArray = this.ordersFormArray.controls.filter(c => !c.value.isPlaceholder);
    if (controlsToProcessData.length === 0) { this.toastr.info('No identified orders to process.', 'No Orders'); return; }

    this.isSubmitting = true; this.cd.markForCheck();
    const promises: Promise<void | null>[] = [];

    for (let i = 0; i < controlsToProcessData.length; i++) {
        const rowData = controlsToProcessData[i];
        const rowGroup = controlsInFormArray[i] as OrderTransferFormGroup;
        if (rowGroup.value.isSuccess) { continue; }

        const orderId = rowData.orderId!;
        const targetOrderStatusId = rowData.targetOrderStatusId!;
        const placeId = rowData.placeId!;

        const payload: ChangeOrderStatusRequest = { 
          statusId: targetOrderStatusId, 
          toPlaceId: placeId 
        };

        rowGroup.controls.isProcessing.setValue(true); rowGroup.controls.isSuccess.setValue(false); rowGroup.controls.errorMessage.setValue(null);
        rowGroup.controls.targetOrderStatusId.disable(); rowGroup.controls.placeId.disable();
        this.cd.markForCheck();

        const promise = firstValueFrom(
            this.orderService.changeOrderStatus(orderId, payload).pipe(
                // Use observer object syntax for tap
                tap({
                    next:(response) => { // Success
                        rowGroup.patchValue({
                            isProcessing: false, isSuccess: true,
                            currentOrderStatusDisplay: this.allOrderStatuses.find(s => s.id === targetOrderStatusId)?.name || null,
                            validStatusTransitions: this.allOrderStatuses.find(s => s.id === targetOrderStatusId)?.validTransitions || [],
                            errorMessage: null
                        });
                    },
                }),
                catchError((error) => { // Error
                    console.error(`Error processing Order ID ${orderId}:`, error);
                    const message = error?.error?.message || error?.message || 'Update failed';
                    rowGroup.patchValue({ isProcessing: false, isSuccess: false, errorMessage: message });
                    rowGroup.controls.targetOrderStatusId.enable(); rowGroup.controls.placeId.enable();
                    return of(null);
                }),
                finalize(() => this.cd.markForCheck())
            )
        );
        promises.push(promise);
    } // --- End for loop ---

    try {
        await Promise.all(promises);
        const successCount = controlsToProcessData.filter((_, i) => controlsInFormArray[i].value.isSuccess).length;
        const errorCount = controlsToProcessData.length - successCount;
        if (errorCount > 0) { this.toastr.warning(`Processed ${successCount} orders, ${errorCount} failed.`, 'Processing Finished'); }
        else { this.toastr.success(`Successfully processed all ${successCount} orders.`, 'Processing Complete'); }
    } catch (error) {
        console.error("Unexpected error awaiting updates:", error); this.toastr.error('Unexpected error during submission.', 'Error');
    } finally {
        this.isSubmitting = false;
        this.cd.markForCheck();
    }
  } // End onSubmit


  // --- Template Helper ---
  public getFormControl(control: AbstractControl | null, controlName: keyof OrderTransferFormRow): FormControl | null {
    if (control instanceof FormGroup) {
      return control.get(controlName) as FormControl | null;
    }
    return null;
  }
}
